import { useCallback, useState, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { formatSubPolicyLabel } from '@kirin/api-client/helpers/alerts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import type { FilterValue } from '@kirin/api-client/contracts/table';
import { AlertOccurrencesPanel } from './AlertOccurrencesPanel';

export interface AlertOccurrencesModalProps {
  open: boolean;
  alertKey: string;
  organizationId?: string;
  filters?: FilterValue[];
  onClose: () => void;
  /** KIR-4785: open filtered to this session (from the Session view). */
  initialSessionId?: string;
}

/**
 * Near-fullscreen modal that shows paginated instances for a grouped alert.
 * Title: "Instances of [slug]" with "N instances" count (KIR-5368: was "Alert
 * Variations"; the other paths are listed below the instances, not counted).
 *
 * KIR-2381: revamp on -> centralized SUB_POLICY_DISPLAY label; off ->
 * legacy raw description for byte-identical off-state.
 */
export function AlertOccurrencesModal({
  open,
  alertKey,
  organizationId,
  filters,
  onClose,
  initialSessionId,
}: AlertOccurrencesModalProps): ReactElement {
  const [titleInfo, setTitleInfo] = useState<{
    type: string;
    description: string;
    subPolicyKey: string | null | undefined;
    total: number;
    totalUnfiltered: number | undefined;
  } | null>(null);
  const handleDataLoaded = useCallback(
    (payload: {
      alert: {
        type: string;
        description: string;
        subPolicyKey?: string | null | undefined;
      };
      pagination: { total: number };
      totalUnfiltered?: number;
    }) =>
      setTitleInfo({
        type: payload.alert.type,
        description: payload.alert.description,
        subPolicyKey: payload.alert.subPolicyKey,
        total: payload.pagination.total,
        totalUnfiltered: payload.totalUnfiltered,
      }),
    []
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setTitleInfo(null);
        onClose();
      }
    },
    [onClose]
  );

  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  const instanceCount = titleInfo ? titleInfo.total : 0;
  const totalUnfilteredCount = titleInfo?.totalUnfiltered;
  const isFiltered =
    totalUnfilteredCount !== undefined &&
    totalUnfilteredCount !== titleInfo?.total;
  const titleText = titleInfo
    ? revampEnabled
      ? formatSubPolicyLabel(titleInfo.subPolicyKey, titleInfo.type)
      : titleInfo.description
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="!max-w-[calc(100%-8rem)] w-[calc(100%-8rem)] h-[calc(100%-8rem)] flex flex-col gap-4 overflow-hidden p-6"
        showCloseButton
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {titleText !== null && titleInfo !== null ? (
              <>
                Instances of {titleText}
                <Badge variant="secondary" className="font-normal">
                  {isFiltered
                    ? `Showing ${titleInfo.total} of ${totalUnfilteredCount} instances`
                    : `${instanceCount} instance${instanceCount !== 1 ? 's' : ''}`}
                </Badge>
              </>
            ) : (
              'Alert instances'
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 min-w-0 flex-1 overflow-auto">
          <AlertOccurrencesPanel
            alertKey={alertKey}
            organizationId={organizationId}
            filters={filters}
            layout="modal"
            onDataLoaded={handleDataLoaded}
            initialSessionId={initialSessionId}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

AlertOccurrencesModal.displayName = 'AlertOccurrencesModal';
